import MenuOptions from '@/@straico-ui/commons/MenuOptions';
import useChatStore, { COLUMNS, GRID, ROWS } from '@/Components/Chat/store';
import CoinsBadge from '@/Features/App/Components/CoinsBadge';
import UserSettings from '@/Features/App/Components/UserSettings';
import ActiveAttachmentFiles from '@/Features/Core/Components/ActiveAttachmentFiles/ActiveAttachmentFiles';
import { GridIcon, RowsIcon, TreeIcon } from '@/Theme/icons/sideBySideIcons';
import ClearIcon from '@/Theme/icons/titleHeaderIcons/clearIcon';
import DeleteIcon from '@/Theme/icons/titleHeaderIcons/deleteIcon';
import DownloadIcon from '@/Theme/icons/titleHeaderIcons/downloadIcon';
import EditIcon from '@/Theme/icons/titleHeaderIcons/editIcon';
import { cn } from '@/utils/styles';
import {
  ArrowLeftIcon,
  EllipsisVerticalIcon,
  EyeIcon,
  EyeSlashIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import { IconButton, Tooltip } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { memo, useMemo } from 'react';

const TitleBarChat = ({
  messages,
  setDeleted,
  goBackChat,
  setToDelete,
  setShowFileDialog,
  setShowEditDialog,
  setShowClearDialog,
  setShowDeleteDialog,
  setShowDownloadDialog,
}) => {
  const {
    refFile,
    renderType,
    hideOnHover,
    setShareChat,
    selectedChat,
    headerVisible,
    setRenderType,
    updateRefFile,
    setHideOnHover,
    selectedModels,
    maxMessageModels,
  } = useChatStore((state) => ({
    refFile: state.refFile,
    renderType: state.renderType,
    hideOnHover: state.hideOnHover,
    selectedChat: state.selectedChat,
    setShareChat: state.setShareChat,
    updateRefFile: state.updateRefFile,
    setRenderType: state.setRenderType,
    headerVisible: state.headerVisible,
    selectedModels: state.selectedModels,
    setHideOnHover: state.setHideOnHover,
    maxMessageModels: state.maxMessageModels,
  }));

  const renderTypes = useMemo(() => {
    const types = [
      { label: 'Tree view', type: COLUMNS, icon: TreeIcon },
      { label: 'Rows layout', type: ROWS, icon: RowsIcon },
      { label: 'Grid layout', type: GRID, icon: GridIcon },
    ];
    return maxMessageModels > 2 || selectedModels.length > 2
      ? types
      : types.filter((e) => e.type != GRID);
  }, [maxMessageModels, selectedModels]);

  return (
    <div className="flex items-start justify-between w-full gap-4 mb-1 md:mb-3">
      <div className="hidden md:block md:min-w-28 lg:min-w-40" />
      <div className="flex flex-col w-full">
        {/* Title chat - Top Bar */}
        <AnimatePresence>
          <div className="flex md:m-auto md:items-center">
            {(messages?.length > 0 || selectedChat?.chat?.title) && (
              <>
                {headerVisible && (
                  <motion.div
                    key="title-actions"
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={{
                      hidden: { height: 0, opacity: 0 },
                      visible: { height: 54, opacity: 1 },
                    }}
                    transition={{ duration: 0.3 }}
                    className="flex px-2 mx-auto w-full md:min-w-[400px] lg:min-w-[600px] lg:max-w-4xl text-violet-blue dark:text-stargate-shimmer bg-lavender h-8 rounded-b-xl dark:bg-ship-grey shadow-[0px_2px_7.6px_0px_#0000001A]"
                  >
                    <Tooltip title="Go back" arrow placement="bottom-end">
                      <div
                        onClick={goBackChat}
                        className="w-10 p-1 flex items-center justify-center hover:bg-ghost-white dark:hover:bg-palladium rounded cursor-pointer lg:hidden"
                      >
                        <ArrowLeftIcon className="h-5 w-5" />
                      </div>
                    </Tooltip>

                    <div className="w-full flex-1 flex items-center justify-center line-clamp-1">
                      <p className="text-xl font-semibold leading-6 line-clamp-1">
                        {selectedChat?.chat?.title}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Tooltip title={hideOnHover ? 'Show' : 'Hide'} arrow>
                        <IconButton
                          onClick={() => setHideOnHover(!hideOnHover)}
                        >
                          {hideOnHover ? (
                            <EyeSlashIcon
                              className={
                                'h-5 w-5 text-violet-blue dark:text-stargate-shimmer stroke-2'
                              }
                            />
                          ) : (
                            <EyeIcon
                              className={
                                'h-5 w-5 text-violet-blue dark:text-stargate-shimmer stroke-2'
                              }
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                      <MenuOptions
                        options={[
                          {
                            label: 'Share',
                            onClick: () => setShareChat(true),
                            icon: (
                              <ShareIcon className="h-5 w-5 text-violet-blue dark:text-stargate-shimmer" />
                            ),
                          },
                          {
                            label: 'Download',
                            onClick: () => setShowDownloadDialog(true),
                            icon: (
                              <DownloadIcon className="h-5 w-5 text-violet-blue dark:text-stargate-shimmer" />
                            ),
                          },
                          {
                            label: 'Delete',
                            onClick: () => {
                              setDeleted(false);
                              setShowDeleteDialog(true);
                              setToDelete(selectedChat?.chat?._id);
                            },
                            icon: (
                              <DeleteIcon className="h-5 w-5 text-violet-blue dark:text-stargate-shimmer" />
                            ),
                          },
                          {
                            label: 'Edit name',
                            onClick: () => setShowEditDialog(true),
                            icon: (
                              <EditIcon className="h-5 w-5 text-violet-blue dark:text-stargate-shimmer" />
                            ),
                          },
                          {
                            label: 'Clear',
                            onClick: () => setShowClearDialog(true),
                            icon: (
                              <ClearIcon className="h-5 w-5 text-violet-blue dark:text-stargate-shimmer" />
                            ),
                          },
                        ]}
                        title={
                          <IconButton>
                            <EllipsisVerticalIcon className="h-5 w-5 text-violet-blue dark:text-stargate-shimmer" />
                          </IconButton>
                        }
                        classNameButton="flex items-center gap-2"
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                        sx={{
                          '& .MuiPaper-root': {
                            borderRadius: 6,
                            minWidth: 180,
                            py: 1,
                            color: 'rgb(55, 65, 81)',
                            boxShadow:
                              'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
                            '& .MuiMenu-list': {
                              padding: '4px 0',
                            },
                          },
                        }}
                      />
                    </div>
                  </motion.div>
                )}
              </>
            )}
            {headerVisible && (
              <div
                className={cn('flex items-center gap-2 h-full mt-2 md:mt-1', {
                  'min-w-[200px] md:min-w-[300px] lg:min-w-[400px] mb-3':
                    messages?.length === 0 && refFile && refFile.length > 0,
                })}
              >
                <ActiveAttachmentFiles
                  files={refFile}
                  messagesCount={messages?.length ?? 0}
                  onFileChange={updateRefFile}
                  setShowFileDialog={setShowFileDialog}
                />
              </div>
            )}
          </div>
        </AnimatePresence>
      </div>
      <div>
        <div className="hidden md:flex items-center mt-2 mr-2">
          {headerVisible && (
            <motion.div
              key="title-settings"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={{
                hidden: { height: 0, opacity: 0 },
                visible: { height: 32, opacity: 1 },
              }}
              transition={{ duration: 0.3 }}
              className="flex items-center"
            >
              <CoinsBadge />
              <UserSettings />
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

const areEqual = (prevProps, nextProps) => {
  return prevProps.messages === nextProps.messages;
};

export default memo(TitleBarChat, areEqual);
